import { useQuery } from "@tanstack/react-query";

export interface B3ProfileSource {
  type: string;
  address: string;
  name: string | null;
  avatar: string | null;
  bio: string | null;
  displayName: string | null;
}

export interface B3Profile {
  name: string | null;
  address: string;
  avatar: string | null;
  bio: string | null;
  displayName: string | null;
  profiles: B3ProfileSource[];
}

async function fetchB3Profile(addressOrName: string): Promise<B3Profile | null> {
  if (!addressOrName) return null;

  // Determine if it's an address or name
  const isAddress = addressOrName.startsWith("0x");
  const param = isAddress ? "address" : "name";

  const url = `https://profiles.b3.fun/?${param}=${addressOrName}`;
  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 404) {
      return null; // No profile found
    }
    throw new Error(`Failed to fetch profile: ${response.statusText}`);
  }

  const data: B3Profile = await response.json();
  return data;
}

interface UseB3ProfileOptions {
  addressOrName?: string;
  enabled?: boolean;
}

export function useB3Profile({ addressOrName, enabled = true }: UseB3ProfileOptions) {
  return useQuery({
    queryKey: ["b3-profile", addressOrName],
    queryFn: () => {
      if (!addressOrName) return null;
      return fetchB3Profile(addressOrName);
    },
    enabled: enabled && Boolean(addressOrName),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}
